const toNumber = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const roundTo = (value, places = 4) => {
  const factor = 10 ** places
  return Math.round((toNumber(value) + Number.EPSILON) * factor) / factor
}

const isTruthyFlag = (value) => (
  value === true || value === 1 || value === '1' || value === 'true'
)

const isStacked = (mode) => {
  if (mode === false || mode === 0 || mode === '0') return false
  return mode !== 'side_by_side'
}

const parseStructureComponents = (value) => {
  if (Array.isArray(value)) return value
  if (!value) return []
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    return []
  }
}

export const WALL_MOUNT_BRACKET_RULES = {
  maxSpacingFt: 6,
  twoBracketMaxWidthFt: 8,
  minBracketsPerRow: 2,
  twoRowMaxHeightFt: 5,
  pairSize: 2,
  optionDescription: 'Wall Mount Brackets (2)'
}

export const isWallMountPairOption = (option) => {
  const description = option?.description || ''
  return /wall\s*mount\s*brackets?\s*\(\s*2\s*\)/i.test(description)
}

export const preferWallMountPairOption = (options = []) => (
  options.find(isWallMountPairOption) || options[0] || null
)

export const getBracketsPerRow = (widthFt) => {
  const width = toNumber(widthFt)
  if (width <= 0) return 0
  if (width <= WALL_MOUNT_BRACKET_RULES.twoBracketMaxWidthFt) {
    return WALL_MOUNT_BRACKET_RULES.minBracketsPerRow
  }
  return Math.ceil(width / WALL_MOUNT_BRACKET_RULES.maxSpacingFt) + 1
}

export const getBracketRowCount = (heightFt) => {
  const height = toNumber(heightFt)
  if (height <= 0) return 0
  return height <= WALL_MOUNT_BRACKET_RULES.twoRowMaxHeightFt ? 2 : 3
}

export const calculateWallMountBrackets = (widthFt, heightFt) => {
  const bracketsPerRow = getBracketsPerRow(widthFt)
  const rowCount = getBracketRowCount(heightFt)
  const totalBrackets = bracketsPerRow * rowCount
  return {
    widthFt: roundTo(widthFt, 2),
    heightFt: roundTo(heightFt, 2),
    bracketsPerRow,
    rowCount,
    totalBrackets,
    pairQuantity: Math.ceil(totalBrackets / WALL_MOUNT_BRACKET_RULES.pairSize)
  }
}

const extraComponentSize = (component = {}) => {
  if (component.type === 'emc') {
    return {
      heightFt: toNumber(component.modsH || component.heightFt),
      lengthFt: toNumber(component.modsL || component.lengthFt)
    }
  }
  return {
    heightFt: roundTo(toNumber(component.heightFt) + (toNumber(component.heightIn) / 12)),
    lengthFt: roundTo(toNumber(component.lengthFt) + (toNumber(component.lengthIn) / 12))
  }
}

export const getSignFaceDimensions = (input = {}) => {
  const src = input || {}
  let stackedHeight = 0
  let stackedWidth = 0
  let sideHeight = 0
  let sideWidth = 0

  const addComponent = (heightFt, lengthFt, stacked) => {
    if (heightFt <= 0 && lengthFt <= 0) return
    if (stacked) {
      stackedHeight += heightFt
      stackedWidth = Math.max(stackedWidth, lengthFt)
    } else {
      sideHeight = Math.max(sideHeight, heightFt)
      sideWidth += lengthFt
    }
  }

  if (!isTruthyFlag(src.noEmc)) {
    const emcHeightFt = toNumber(
      src.emcModsH || src.emcHeightFt || src.emcPricing?.modsH
    )
    const emcLengthFt = toNumber(
      src.emcModsL || src.emcLengthFt || src.emcPricing?.modsL
    )
    addComponent(emcHeightFt, emcLengthFt, isStacked(src.emcStackMode))
  }

  if (!isTruthyFlag(src.noCabinet)) {
    const cabinetHeightFt = roundTo(
      toNumber(src.cabinetHeightFt) + (toNumber(src.cabinetHeightIn) / 12)
    )
    const cabinetLengthFt = roundTo(
      toNumber(src.cabinetLengthFt) + (toNumber(src.cabinetLengthIn) / 12)
    )
    addComponent(cabinetHeightFt, cabinetLengthFt, isStacked(src.cabinetStackMode))
  }

  parseStructureComponents(src.structureComponents).forEach((component) => {
    const size = extraComponentSize(component)
    addComponent(size.heightFt, size.lengthFt, isStacked(component.stackMode ?? component.stacked))
  })

  return {
    heightFt: roundTo(Math.max(stackedHeight, sideHeight), 2),
    lengthFt: roundTo(stackedWidth + sideWidth, 2)
  }
}

export const calculateWallMountBracketsFromQuote = (input = {}) => {
  const { heightFt, lengthFt } = getSignFaceDimensions(input)
  return calculateWallMountBrackets(lengthFt, heightFt)
}

export const formatWallMountBracketSummary = (calc) => {
  if (!calc || !calc.pairQuantity) return 'Enter sign width and height to calculate bracket pairs.'
  return `${calc.bracketsPerRow} per row × ${calc.rowCount} rows = ${calc.totalBrackets} brackets (${calc.pairQuantity} pair${calc.pairQuantity === 1 ? '' : 's'})`
}
